import { psuInstallLength } from './geometry.js';

// Logical configuration branches sit between compatibility data and spatial placement.
// They resolve mutually conditional motherboard / PSU / cooling combinations before
// any component receives a physical XYZ pose.

function finite(value, fallback = null) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function normalizeMobo(value) {
  const raw = String(value || '').trim().toLowerCase()
    .replace(/micro[-_ ]?atx|m[-_ ]?atx/g, 'matx')
    .replace(/mini[-_ ]?itx/g, 'itx');
  if (raw.includes('matx')) return 'MATX';
  if (raw.includes('dtx')) return 'DTX';
  if (raw.includes('atx') && !raw.includes('itx')) return 'ATX';
  if (raw.includes('itx')) return 'ITX';
  return '';
}

export function normalizePsu(value) {
  const raw = String(value || '').trim().toLowerCase().replace(/\s+/g, '').replace(/-/g, '_');
  if (raw.includes('sfx_l') || raw.includes('sfxl')) return 'SFX-L';
  if (raw.includes('flex')) return 'FLEX';
  if (raw === 'atx' || (raw.includes('atx') && !raw.includes('sfx'))) return 'ATX';
  if (raw.includes('sfx')) return 'SFX';
  return '';
}

export function normalizeCooling(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  if (raw.includes('aio') || raw.includes('liquid') || raw.includes('water')) return 'liquid';
  if (raw.includes('air')) return 'air';
  return raw;
}

export function normalizeRadFan(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value <= 17) return 'slim';
    if (value >= 20) return 'standard';
  }
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  if (raw.includes('slim') || raw === '15' || raw === '15mm') return 'slim';
  if (raw.includes('standard') || raw.includes('normal') || raw === '25' || raw === '25mm') return 'standard';
  return raw;
}

function valueList(value) {
  return Array.isArray(value) ? value : [value];
}

function normalizedExpectedValues(value, normalizer = v => String(v)) {
  return valueList(value)
    .filter(v => v !== null && v !== undefined && v !== '')
    .map(normalizer)
    .filter(Boolean);
}

function matchScalar(actual, expected, normalizer) {
  const expectedValues = normalizedExpectedValues(expected, normalizer);
  if (!expectedValues.length) return true;
  const actualValue = normalizer(actual);
  return !!actualValue && expectedValues.includes(actualValue);
}

function compareThreshold(actual, op, expected) {
  const a = finite(actual), e = finite(expected);
  if (a === null || e === null) return false;
  if (op === 'gt') return a > e;
  if (op === 'gte') return a >= e;
  if (op === 'lt') return a < e;
  if (op === 'lte') return a <= e;
  return false;
}

export function ruleMatchesConfiguration(rule, context = {}) {
  const conditions = rule?.conditions || {};
  for (const [key, expected] of Object.entries(conditions)) {
    if (expected === null || expected === undefined || expected === '') continue;
    if (key === 'mobo') {
      if (!matchScalar(context.mobo, expected, normalizeMobo)) return false;
      continue;
    }
    if (key === 'psu') {
      if (!matchScalar(context.psu, expected, normalizePsu)) return false;
      continue;
    }
    if (key === 'cooling') {
      if (!matchScalar(context.cooling, expected, normalizeCooling)) return false;
      continue;
    }
    if (key === 'rad' || key === 'radiator') {
      const actual = finite(context.rad, null);
      const expectedValues = normalizedExpectedValues(expected, v => finite(v, null)).filter(v => v !== null);
      if (!expectedValues.length) continue;
      if (actual === null || !expectedValues.some(v => Math.abs(v - actual) < 0.01)) return false;
      continue;
    }
    if (key === 'radFan') {
      if (!matchScalar(context.radFan, expected, normalizeRadFan)) return false;
      continue;
    }
    const threshold = key.match(/^(gpuT|gpuL|gpuH|cpuH)_(gt|gte|lt|lte)$/);
    if (threshold) {
      if (!compareThreshold(context[threshold[1]], threshold[2], expected)) return false;
    }
  }
  return true;
}

function applyOverrides(base, overrides = {}) {
  const out = { ...base };
  const aliases = { maxThick:'maxGPUT' };
  for (const [rawKey, rawValue] of Object.entries(overrides || {})) {
    const key = aliases[rawKey] || rawKey;
    if (!(key in out)) continue;
    const n = finite(rawValue, null);
    if (n === null || n < 0) continue;
    // Dynamic rules are restrictive against the active case/mode state.
    if (out[key] > 0 && n > 0) out[key] = Math.min(out[key], n);
    else out[key] = n;
  }
  return out;
}

function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

function selectedDescriptor(components, type) {
  return (components || []).find(component => component?.type === type && component?.actual) || null;
}

function selectedContext(components = []) {
  const mobo = selectedDescriptor(components, 'motherboard');
  const psu = selectedDescriptor(components, 'psu');
  const gpu = selectedDescriptor(components, 'gpu');
  const cooler = selectedDescriptor(components, 'aircooler');
  const radiator = selectedDescriptor(components, 'radiator');
  return {
    mobo: normalizeMobo(mobo?.formFactor),
    psu: normalizePsu(psu?.formFactor),
    cooling: radiator ? 'liquid' : (cooler ? 'air' : ''),
    rad: finite(radiator?.radiatorSize, 0) || 0,
    radFan: normalizeRadFan(radiator?.radFan || radiator?.fanProfile || 'standard'),
    fanThickness: finite(radiator?.fanThickness, null),
    gpuL: finite(gpu?.dims?.l, null),
    gpuT: finite(gpu?.dims?.w, null),
    gpuH: finite(gpu?.dims?.h, null),
    cpuH: finite(cooler?.dims?.h, null),
    psuL: psu ? psuInstallLength(psu.dims, psu.formFactor) : null
  };
}

function ruleFanAlternatives(overlay, context) {
  const selectedProfile=normalizeRadFan(context?.radFan || 'standard') || 'standard';
  const selectedThickness=finite(context?.fanThickness,null);
  const standard = { profile:'standard', thickness:selectedProfile==='standard' && selectedThickness>0 ? selectedThickness : 25, priority:20, reasons:[selectedThickness>0&&selectedProfile==='standard'?'catalog_fan_thickness':'default_25mm_fan_stack'] };
  const slim = { profile:'slim', thickness:selectedProfile==='slim' && selectedThickness>0 ? selectedThickness : 15, priority:10, reasons:[selectedThickness>0&&selectedProfile==='slim'?'catalog_slim_fan_thickness':'slim_fan_alternative'] };
  let standardFailed = false;
  let standardWarned = false;

  // If the selected AIO already ships with slim fans, preserve that physical stack. Case
  // rules can still reject the branch later, but do not silently inflate it to 25 mm.
  if(selectedProfile==='slim') return [slim];

  for (const rule of overlay?.rules || []) {
    const conditions = rule?.conditions || {};
    if (conditions.radFan === null || conditions.radFan === undefined || conditions.radFan === '') continue;
    const probe = { ...context, radFan:'standard', fanThickness:standard.thickness };
    if (!ruleMatchesConfiguration(rule, probe)) continue;
    if (normalizeRadFan(conditions.radFan) !== 'standard') continue;
    if (rule?.fail_message) standardFailed = true;
    else if (rule?.warn_message) standardWarned = true;
  }

  if (standardFailed) return [{ ...slim, priority:40, reasons:[...slim.reasons,'standard_fan_rule_failed'] }];
  if (standardWarned) return [
    { ...slim, priority:35, reasons:[...slim.reasons,'standard_fan_rule_warned'] },
    { ...standard, priority:12, reasons:[...standard.reasons,'standard_fan_rule_warned'] }
  ];
  return [standard];
}

function ruleUsesSelectedGeometry(rule) {
  return Object.keys(rule?.conditions || {}).some(key => /^(gpuT|gpuL|gpuH|cpuH)_(gt|gte|lt|lte)$/.test(key));
}

function topologyPsuPolicy(effective, profile, context) {
  const explicit = String(
    effective?.spatialHints?.psuOrientation || effective?.spatialHints?.psuPose || effective?.spatialHints?.psuPolicy || ''
  ).trim().toLowerCase();
  if (explicit) {
    // Structured hints may choose ordinary vs vertical, but they no longer unlock arbitrary
    // axis permutations. Facing/reverse is represented by sign-equivalent candidates.
    if (explicit.includes('upright') || explicit.includes('vertical')) return { id:'explicit_vertical', orientations:'upright', region:'structured', allowUprightFallback:true, reason:'structured_spatial_hint' };
    return { id:'explicit_canonical', orientations:'horizontal', region:'structured', reason:'structured_spatial_hint' };
  }

  const family = String(effective?.layoutFamily || 'reference').toLowerCase();
  const uprightSandwich = family === 'sandwich'
    && (effective?.modeTraits?.uprightSandwich === true || effective?.physicalLayoutId === 'vertical_sandwich');
  if (uprightSandwich) {
    return { id:'vertical_sandwich_canonical', orientations:'upright', region:'sandwich_residual', allowUprightFallback:true, reason:'upright_sandwich_long_axis_y' };
  }
  if (family === 'sandwich') {
    return { id:'sandwich_canonical', orientations:'horizontal', region:'sandwich_residual', reason:'canonical_psu_axis' };
  }
  if (family === 'console') {
    // S5M is the canonical non-vertical PSU pose. Console topology changes the mount region,
    // not the PSU axis assignment.
    return { id:'console_canonical', orientations:'horizontal', region:'console_lower_end', reason:'canonical_psu_axis' };
  }

  // Full-size ATX PSUs are not the same packaging problem as front-hung SFX/SFX-L.
  // In a reference layout they should keep the canonical PSU axis and search the complete
  // residual enclosure. Front/bottom mounting can still win by score, but it is no longer a
  // hard region constraint. Explicit structured hints above remain authoritative.
  if (family === 'reference' && context.psu === 'ATX') {
    return { id:'reference_atx_residual', orientations:'horizontal', region:'reference_atx_residual', reason:'reference_atx_broad_residual' };
  }

  const compactReference = family === 'reference' && ['MATX','ATX'].includes(context.mobo)
    && ['SFX','SFX-L'].includes(context.psu) && (profile?.psuTypes || []).includes('ATX');
  if (compactReference) {
    return { id:'large_board_sfx_front_lateral', orientations:'front_facing', region:'large_board_front_lateral', reason:'large_board_sfx_shared_cavity' };
  }
  return { id:'reference_front_residual', orientations:'horizontal', region:'reference_front_residual', reason:'reference_front_residual' };
}

function failureOwnersForRule(rule) {
  const owners=new Set();
  const keys=[...Object.keys(rule?.conditions||{}),...Object.keys(rule?.overrides||{})];
  for(const rawKey of keys){
    const key=String(rawKey||'').toLowerCase();
    if(key==='mobo'||key.includes('motherboard'))owners.add('motherboard');
    if(key==='psu'||key==='maxpsu'||key.startsWith('psu'))owners.add('psu');
    if(key.startsWith('gpu')||key.includes('graphics'))owners.add('gpu');
    if(key.startsWith('cpu')||key.includes('cooler'))owners.add('aircooler');
    if(key==='rad'||key.includes('radiator')||key.includes('aio')||key.includes('cooling')||key.includes('fan'))owners.add('radiator');
  }
  return [...owners];
}

function branchPriority({ selected, cooling, mobo, psu, fanProfile, valid }) {
  let score = valid ? 100 : -1000;
  if (selected.mobo && selected.mobo === mobo) score += 60;
  if (selected.psu && selected.psu === psu) score += 60;
  if (selected.cooling && selected.cooling === cooling) score += 55;
  if (!selected.cooling && cooling === 'liquid') score += 14; // reserve the harder cooling-capable branch first
  if (fanProfile === 'slim') score += 1;
  return score;
}

export function buildConfigurationBranches({ caseObj, overlay, effective, capabilityProfile, components = [] } = {}) {
  const selected = selectedContext(components);
  const mobos = selected.mobo ? [selected.mobo] : unique(capabilityProfile?.motherboardTypes || ['ITX']);
  const psus = selected.psu ? [selected.psu] : unique(capabilityProfile?.psuTypes || ['SFX']);
  const supportsLiquid = finite(effective?.limits?.maxAIO, 0) > 0;
  const coolingModes = selected.cooling ? [selected.cooling] : (supportsLiquid ? ['liquid','air'] : ['air']);
  const branches = [];
  let serial = 0;

  for (const mobo of mobos) {
    for (const psu of psus) {
      for (const cooling of coolingModes) {
        const rad = cooling === 'liquid' ? (selected.rad || finite(effective?.limits?.maxAIO, 0) || 0) : 0;
        const baseContext = {
          ...selected,
          mobo,
          psu,
          cooling,
          rad,
          radFan: selected.radFan || 'standard'
        };
        // Cooling capability shapes the chassis even when the CURRENT build is air cooled.
        // Derive the structural radiator/fan profile from the case's liquid-capable branch,
        // then keep actual cooling identity separate. This prevents the board/PSU from
        // moving when a cooler is swapped for an AIO later.
        const structuralRad = supportsLiquid ? (selected.rad || finite(effective?.limits?.maxAIO,0) || 0) : 0;
        const structuralProbe = { ...baseContext, cooling:'liquid', rad:structuralRad, radFan:selected.radFan || 'standard', fanThickness:selected.fanThickness };
        const structuralFanVariants = supportsLiquid ? ruleFanAlternatives(overlay, structuralProbe) : [{profile:'none',thickness:0,priority:0,reasons:[]}];
        const fanVariants = cooling === 'liquid' ? structuralFanVariants : [structuralFanVariants[0] || {profile:'none',thickness:0,priority:0,reasons:[]}];

        for (const fan of fanVariants) {
          const context = { ...baseContext, radFan:cooling === 'liquid' ? fan.profile : 'none' };
          let structuralLimits = { ...(effective?.limits || {}) };
          let validationLimits = { ...(effective?.validationLimits || effective?.limits || {}) };
          const matchedRules = [];
          const failures = [];
          const failureDetails = [];
          const warnings = [];
          const pushFailure=(message,owners=[],source='branch')=>{
            const text=String(message||'configuration branch invalid');
            failures.push(text);
            failureDetails.push({message:text,owners:[...new Set((owners||[]).filter(Boolean))],source});
          };

          for (const rule of overlay?.rules || []) {
            if (!ruleMatchesConfiguration(rule, context)) continue;
            matchedRules.push(String(rule?.rule_id || rule?.id || `rule_${matchedRules.length}`));
            const overrides = rule?.overrides || null;
            validationLimits = applyOverrides(validationLimits, overrides || {});
            // Selected GPU/cooler dimensions can tighten compatibility, but they must not move
            // fixed case structure. Keep those shape-triggered overrides out of the structural
            // envelope so a shorter installed GPU never creates imaginary motherboard travel.
            if(!ruleUsesSelectedGeometry(rule)) structuralLimits = applyOverrides(structuralLimits, overrides || {});

            const violations = overrides ? [
              overrides.maxGPUT !== undefined && finite(context.gpuT,null) !== null && finite(context.gpuT,0) > finite(validationLimits.maxGPUT,Infinity),
              overrides.maxGPU !== undefined && finite(context.gpuL,null) !== null && finite(context.gpuL,0) > finite(validationLimits.maxGPU,Infinity),
              overrides.maxGPUH !== undefined && finite(context.gpuH,null) !== null && finite(context.gpuH,0) > finite(validationLimits.maxGPUH,Infinity),
              overrides.maxCPU !== undefined && finite(context.cpuH,null) !== null && finite(context.cpuH,0) > finite(validationLimits.maxCPU,Infinity),
              overrides.maxAIO !== undefined && finite(context.rad,null) !== null && finite(context.rad,0) > finite(validationLimits.maxAIO,Infinity),
              overrides.maxPSU !== undefined && finite(context.psuL,null) !== null && finite(context.psuL,0) > finite(validationLimits.maxPSU,Infinity)
            ].some(Boolean) : false;
            if (rule?.fail_message && (!overrides || violations)) pushFailure(String(rule.fail_message),failureOwnersForRule(rule),`rule:${String(rule?.rule_id||rule?.id||matchedRules.length-1)}`);
            if (rule?.warn_message) warnings.push(String(rule.warn_message));
          }

          const motherboardSupported = !capabilityProfile?.motherboardSupportKnown || (capabilityProfile?.motherboardTypes || []).includes(mobo);
          const psuSupported = !capabilityProfile?.psuSupportKnown || (capabilityProfile?.psuTypes || []).includes(psu);
          if (!motherboardSupported) pushFailure(`${mobo} motherboard unsupported`,['motherboard'],'declared_support');
          if (!psuSupported) pushFailure(`${psu} PSU unsupported`,['psu'],'declared_support');
          if (cooling === 'liquid' && !(validationLimits.maxAIO > 0) ) pushFailure('liquid cooling unavailable in branch',['radiator'],'cooling_capability');
          if (cooling === 'liquid' && rad > 0 && validationLimits.maxAIO > 0 && rad > validationLimits.maxAIO + 0.01) pushFailure(`radiator ${rad} exceeds branch max ${validationLimits.maxAIO}`,['radiator'],'cooling_capability');

          const valid = failures.length === 0;
          const psuPolicy = topologyPsuPolicy(effective, capabilityProfile, context);
          const id = `cfg_${serial++}_${mobo}_${psu}_${cooling}_${rad || 0}_${fan.profile}`.replace(/[^a-zA-Z0-9_-]+/g,'_');
          const branch = {
            id,
            mobo,
            psu,
            cooling,
            rad,
            radFan:cooling === 'liquid' ? fan.profile : 'none',
            fanThickness:cooling === 'liquid' ? fan.thickness : 0,
            reserveRad:structuralRad,
            reserveRadFan:supportsLiquid ? fan.profile : 'none',
            reserveFanThickness:supportsLiquid ? fan.thickness : 0,
            limits:structuralLimits,
            structuralLimits,
            validationLimits,
            psuPolicy,
            matchedRules,
            failures,
            failureDetails,
            warnings,
            valid,
            reasons:[...(supportsLiquid ? fan.reasons : []), psuPolicy.reason],
            selectedMatch:(!selected.mobo || selected.mobo===mobo) && (!selected.psu || selected.psu===psu) && (!selected.cooling || selected.cooling===cooling)
          };
          branch.priority = branchPriority({selected,cooling,mobo,psu,fanProfile:fan.profile,valid}) + fan.priority;
          branches.push(branch);
        }
      }
    }
  }

  // Keep branch count bounded for live solving. Selected and valid branches always win the
  // sort before broader capability alternatives.
  return branches.sort((a,b)=>b.priority-a.priority).slice(0,32);
}

export function branchEffectiveState(effective, branch) {
  return {
    ...effective,
    limits:{...(branch?.structuralLimits || branch?.limits || effective?.limits || {})},
    validationLimits:{...(branch?.validationLimits || effective?.validationLimits || branch?.limits || effective?.limits || {})},
    configurationBranch:branch || null,
    spatialHints:{
      ...(effective?.spatialHints || {}),
      psuOrientationPolicy:branch?.psuPolicy?.orientations || effective?.spatialHints?.psuOrientationPolicy || 'horizontal',
      psuRegionPolicy:branch?.psuPolicy?.region || effective?.spatialHints?.psuRegionPolicy || 'topology_default',
      psuAllowUprightFallback:branch?.psuPolicy?.allowUprightFallback === true || effective?.spatialHints?.psuAllowUprightFallback === true,
      radFanProfile:branch?.reserveRadFan || branch?.radFan || effective?.spatialHints?.radFanProfile || 'standard',
      radFanThickness:finite(branch?.reserveFanThickness, finite(branch?.fanThickness,25))
    }
  };
}
